import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Bot,
  ChevronDown,
  Copy,
  LayoutDashboard,
  Lock,
  Plus,
  ScrollText,
  ShieldCheck,
  ShieldHalf,
  Terminal,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useScrollToHash } from '@/lib/hooks';
import { lockingRun, upsertRun, useRuns, useShownRun, type BotRun } from '@/lib/runs';
import { PLUGIN_GROUPS, PLUGINS, type PluginKey } from '@/lib/plugins';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AreaSwitches, DEFAULT_AREAS, RunProgress, type BackupArea } from '@/components/BackupParts';

/**
 * Admin-Panel — entry hub for global administration (admin-only). Metrics,
 * links to the existing admin pages, and per-server plugin toggles. The
 * established pages (Zugriff, Command-Perms) stay where they are; this is
 * a launch pad, not a consolidation.
 */

interface AdminGuild {
  id: string;
  name: string;
  iconUrl: string | null;
  disabledPlugins: string[];
  /** Slash commands locked on this server (hidden in Discord). */
  disabledCommands: string[];
  /** Aktions-Log switch (default on). */
  actionLogEnabled: boolean;
}

interface CommandInfo {
  name: string;
  description: string;
  /** Plugin switch it hangs on; null = always there. */
  plugin: string | null;
}

interface OverviewResponse {
  metrics: {
    dashboardUserCount: number;
    activeGuildCount: number;
    disabledPluginCount: number;
  };
  guilds: AdminGuild[];
}

const OVERVIEW_KEY = ['admin-overview'];
const OPEN_CARDS_KEY = 'adminPanelOpenCards';

/**
 * Which foldable cards are open (ids like `bot-profile`, `clone`,
 * `guild-<id>`) — remembered in this browser. A link to `#clone` /
 * `#bot-profile` opens that card.
 */
function useOpenCards() {
  const { hash } = useLocation();
  const [open, setOpen] = useState<Set<string>>(() => {
    try {
      const list: unknown = JSON.parse(localStorage.getItem(OPEN_CARDS_KEY) ?? '[]');
      return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    if (id) setOpen((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, [hash]);

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      try {
        localStorage.setItem(OPEN_CARDS_KEY, JSON.stringify([...next]));
      } catch {
        // storage blocked — it just won't be remembered
      }
      return next;
    });

  return { isOpen: (id: string) => open.has(id), toggle };
}

function useCommandCatalog() {
  return useQuery({
    queryKey: ['admin-commands'],
    queryFn: () => api<{ commands: CommandInfo[] }>('/api/admin/commands'),
    staleTime: 5 * 60_000,
  });
}

/** Slash commands still showing in Discord on that server. */
function visibleCommandCount(commands: CommandInfo[], guild: AdminGuild) {
  const locked = new Set(guild.disabledCommands);
  return commands.filter((c) => !locked.has(c.name) && !(c.plugin && guild.disabledPlugins.includes(c.plugin))).length;
}

/**
 * A card that folds down to one line: icon, title and a short summary.
 * Clicking the line opens the body; `actions` (links) sit beside it.
 */
function FoldCard({
  id,
  open,
  onToggle,
  icon,
  title,
  summary,
  actions,
  className,
  children,
}: {
  id: string;
  open: boolean;
  onToggle: () => void;
  icon: ReactNode;
  title: ReactNode;
  summary?: ReactNode;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Card id={id} className={className}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 basis-56 items-center gap-3 text-left"
        >
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', !open && '-rotate-90')}
          />
          {icon}
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold">{title}</div>
            {summary && <div className="truncate text-xs text-muted-foreground">{summary}</div>}
          </div>
        </button>
        {actions}
      </div>
      {open && <CardContent className="border-t pt-4 max-md:pt-4">{children}</CardContent>}
    </Card>
  );
}

export function AdminPanel() {
  const { data, isLoading, error } = useQuery({
    queryKey: OVERVIEW_KEY,
    queryFn: () => api<OverviewResponse>('/api/admin/overview'),
  });
  const cards = useOpenCards();

  if (isLoading) return <p className="text-muted-foreground">Lade Admin-Panel…</p>;
  if (error) return <p className="text-destructive">Fehler: {(error as Error).message}</p>;
  if (!data) return null;

  const { metrics, guilds } = data;

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="mb-6 text-2xl font-semibold">Admin-Panel</h1>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile label="Dashboard-User" value={metrics.dashboardUserCount} />
        <StatTile label="Aktive Server" value={metrics.activeGuildCount} />
        <StatTile label="Deaktivierte Schalter (alle Server)" value={metrics.disabledPluginCount} />
      </div>

      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Link to="/access">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Zugriff
              </CardTitle>
              <CardDescription>
                Wer ins Dashboard darf, Rollen, Server-Einschränkungen und Rechte.
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
        <Link to="/logs">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Terminal className="h-4 w-4 text-primary" />
                Live Log
              </CardTitle>
              <CardDescription>Bot-Logausgabe in Echtzeit (nur für Admins).</CardDescription>
            </CardHeader>
          </Card>
        </Link>
        <Link to="/admin/actions">
          <Card className="h-full transition-colors hover:border-primary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ScrollText className="h-4 w-4 text-primary" />
                Aktions-Log
              </CardTitle>
              <CardDescription>Wer hat im Dashboard was geändert oder ausgeführt.</CardDescription>
            </CardHeader>
          </Card>
        </Link>
        <Card className="h-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldHalf className="h-4 w-4 text-primary" />
              Command-Perms
            </CardTitle>
            <CardDescription>
              Pro Server — Link direkt bei jedem Server unten.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>

      <div className="mb-8 space-y-3">
        <BotProfileCard open={cards.isOpen('bot-profile')} onToggle={() => cards.toggle('bot-profile')} />
        <CloneCard guilds={guilds} open={cards.isOpen('clone')} onToggle={() => cards.toggle('clone')} />
      </div>

      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Schalter pro Server
      </h2>
      {guilds.length === 0 ? (
        <p className="text-sm text-muted-foreground">Der Bot ist auf keinem Server.</p>
      ) : (
        <div className="space-y-3">
          {guilds.map((g) => (
            <GuildPluginCard
              key={g.id}
              guild={g}
              open={cards.isOpen(`guild-${g.id}`)}
              onToggle={() => cards.toggle(`guild-${g.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GuildPluginCard({ guild, open, onToggle }: { guild: AdminGuild; open: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const disabled = new Set(guild.disabledPlugins);
  const commands = useCommandCatalog().data?.commands;

  const mut = useMutation({
    mutationFn: (disabledPlugins: string[]) =>
      api<{ disabledPlugins: string[] }>(`/api/admin/guilds/${guild.id}/plugins`, {
        method: 'PUT',
        body: JSON.stringify({ disabledPlugins }),
      }),
    // Optimistic: flip the switch immediately, roll back on error.
    onMutate: async (disabledPlugins) => {
      await qc.cancelQueries({ queryKey: OVERVIEW_KEY });
      const prev = qc.getQueryData<OverviewResponse>(OVERVIEW_KEY);
      if (prev) {
        const guilds = prev.guilds.map((g) => (g.id === guild.id ? { ...g, disabledPlugins } : g));
        qc.setQueryData<OverviewResponse>(OVERVIEW_KEY, {
          ...prev,
          guilds,
          metrics: {
            ...prev.metrics,
            disabledPluginCount: guilds.reduce((n, x) => n + x.disabledPlugins.length, 0),
          },
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(OVERVIEW_KEY, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: OVERVIEW_KEY });
      // Server sidebar + config pages read disabledPlugins from these.
      qc.invalidateQueries({ queryKey: ['guildSettings', guild.id] });
      qc.invalidateQueries({ queryKey: ['settings', guild.id] });
    },
  });

  const logMut = useMutation({
    mutationFn: (enabled: boolean) =>
      api(`/api/admin/guilds/${guild.id}/action-log`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      }),
    onSettled: () => qc.invalidateQueries({ queryKey: OVERVIEW_KEY }),
  });

  const toggle = (key: PluginKey, enabled: boolean) => {
    const next = new Set(disabled);
    if (enabled) next.delete(key);
    else next.add(key);
    mut.mutate(PLUGINS.map((p) => p.key).filter((k) => next.has(k)));
  };

  return (
    <FoldCard
      id={`guild-${guild.id}`}
      open={open}
      onToggle={onToggle}
      icon={
        guild.iconUrl ? (
          <img src={guild.iconUrl} alt="" className="h-9 w-9 shrink-0 rounded-full" />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted">
            <Bot className="h-4 w-4 text-muted-foreground" />
          </div>
        )
      }
      title={guild.name}
      summary={
        <>
          {PLUGINS.length - disabled.size} / {PLUGINS.length} Schalter an
          {commands && commands.length > 0 && (
            <>
              {' '}
              · {visibleCommandCount(commands, guild)} / {commands.length} Commands
            </>
          )}
          {!guild.actionLogEnabled && ' · Aktions-Log aus'}
        </>
      }
      actions={
        <div className="flex gap-3 text-xs max-sm:pl-7">
          <Link
            to={`/server/${guild.id}`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
          </Link>
          <Link
            to={`/server/${guild.id}/command-perms`}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
          >
            <ShieldHalf className="h-3.5 w-3.5" /> Command-Perms
          </Link>
        </div>
      }
    >
      <div className="space-y-4">
        {PLUGIN_GROUPS.map((group) => (
          <div key={group.key}>
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {PLUGINS.filter((p) => p.group === group.key).map((p) => {
                const enabled = !disabled.has(p.key);
                return (
                  <div
                    key={p.key}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className={enabled ? 'text-sm' : 'text-sm text-muted-foreground'}>
                        {p.label}
                        {!enabled && (
                          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
                            deaktiviert
                          </span>
                        )}
                      </div>
                      {(p.hint || p.kind === 'page') && (
                        <div className="truncate text-[11px] text-muted-foreground">
                          {p.hint ?? 'nur Dashboard-Seite'}
                        </div>
                      )}
                    </div>
                    <Switch
                      checked={enabled}
                      disabled={mut.isPending}
                      onChange={(e) => toggle(p.key, e.target.checked)}
                      aria-label={`${p.label} ${enabled ? 'deaktivieren' : 'aktivieren'}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div>
          <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Protokoll
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div className="min-w-0">
                <div className={guild.actionLogEnabled ? 'text-sm' : 'text-sm text-muted-foreground'}>Aktions-Log</div>
                <div className="truncate text-[11px] text-muted-foreground">
                  {guild.actionLogEnabled ? 'Änderungen werden protokolliert' : 'Für diesen Server wird nichts gespeichert'}
                </div>
              </div>
              <Switch
                checked={guild.actionLogEnabled}
                disabled={logMut.isPending}
                onChange={(e) => logMut.mutate(e.target.checked)}
                aria-label={`Aktions-Log ${guild.actionLogEnabled ? 'ausschalten' : 'einschalten'}`}
              />
            </div>
          </div>
        </div>
        {(mut.error || logMut.error) && (
          <p className="mt-2 text-sm text-destructive">{((mut.error || logMut.error) as Error).message}</p>
        )}
        <CommandSwitches guild={guild} disabledPlugins={disabled} />
      </div>
    </FoldCard>
  );
}

/**
 * Slash commands of one server: a switch per command, grouped by plugin.
 * Locked ones vanish from Discord on that server right away (the bot
 * registers commands per server, bot lib/commandSync.js); those of a
 * switched-off plugin are gone anyway.
 */
function CommandSwitches({ guild, disabledPlugins }: { guild: AdminGuild; disabledPlugins: Set<string> }) {
  const qc = useQueryClient();
  const catalog = useCommandCatalog();
  const locked = new Set(guild.disabledCommands);
  const commands = catalog.data?.commands ?? [];

  const mut = useMutation({
    mutationFn: (disabledCommands: string[]) =>
      api(`/api/admin/guilds/${guild.id}/commands`, { method: 'PUT', body: JSON.stringify({ disabledCommands }) }),
    // Optimistic like the plugin switches.
    onMutate: async (disabledCommands) => {
      await qc.cancelQueries({ queryKey: OVERVIEW_KEY });
      const prev = qc.getQueryData<OverviewResponse>(OVERVIEW_KEY);
      if (prev) {
        qc.setQueryData<OverviewResponse>(OVERVIEW_KEY, {
          ...prev,
          guilds: prev.guilds.map((g) => (g.id === guild.id ? { ...g, disabledCommands } : g)),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(OVERVIEW_KEY, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: OVERVIEW_KEY }),
  });

  const toggle = (name: string, on: boolean) => {
    const next = new Set(locked);
    if (on) next.delete(name);
    else next.add(name);
    mut.mutate([...next].sort());
  };

  const pluginLabel = (key: string | null) => (key ? PLUGINS.find((p) => p.key === key)?.label ?? key : 'Allgemein');
  const groups = [...new Set(commands.map((c) => c.plugin))]
    .map((plugin) => ({ plugin, label: pluginLabel(plugin), commands: commands.filter((c) => c.plugin === plugin) }))
    .sort((a, b) => (a.plugin === null ? -1 : b.plugin === null ? 1 : a.label.localeCompare(b.label)));
  const visible = visibleCommandCount(commands, guild);

  return (
    <details className="rounded-md border">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm">
        Slash-Commands{' '}
        <span className="text-muted-foreground">
          · {visible} / {commands.length} sichtbar
        </span>
      </summary>
      <div className="space-y-4 border-t p-3">
        <p className="text-xs text-muted-foreground">
          Ausgeschaltete Commands verschwinden auf diesem Server sofort aus Discord. Commands eines abgeschalteten
          Plugins sind automatisch weg.
        </p>
        {catalog.isLoading && <p className="text-sm text-muted-foreground">Lade…</p>}
        {catalog.data && commands.length === 0 && (
          <p className="text-sm text-muted-foreground">Noch keine Command-Liste — der Bot meldet sie beim nächsten Start.</p>
        )}
        {groups.map((group) => {
          const pluginOff = !!group.plugin && disabledPlugins.has(group.plugin);
          return (
            <div key={group.plugin ?? 'core'}>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {group.label}
                {pluginOff && ' · Plugin aus'}
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {group.commands.map((c) => {
                  const on = !locked.has(c.name) && !pluginOff;
                  return (
                    <div key={c.name} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                      <div className="min-w-0">
                        <div className={on ? 'text-sm' : 'text-sm text-muted-foreground'}>/{c.name}</div>
                        <div className="truncate text-[11px] text-muted-foreground">
                          {pluginOff ? 'Plugin ist abgeschaltet' : c.description}
                        </div>
                      </div>
                      <Switch
                        checked={on}
                        disabled={pluginOff || mut.isPending}
                        onChange={(e) => toggle(c.name, e.target.checked)}
                        aria-label={`/${c.name} ${on ? 'sperren' : 'freigeben'}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {mut.error && <p className="text-sm text-destructive">{(mut.error as Error).message}</p>}
      </div>
    </details>
  );
}

type PresenceType = 'playing' | 'listening' | 'watching' | 'custom';
interface PresenceConfig {
  status: 'online' | 'idle' | 'dnd';
  intervalSec: number;
  entries: Array<{ type: PresenceType; text: string }>;
}
interface BotProfile {
  name: string;
  avatarUrl: string | null;
  description: string;
  presence: PresenceConfig;
}

const PRESENCE_TYPES: Array<{ key: PresenceType; label: string }> = [
  { key: 'playing', label: 'Spielt' },
  { key: 'listening', label: 'Hört' },
  { key: 'watching', label: 'Schaut' },
  { key: 'custom', label: 'Eigener Text' },
];
const MAX_ENTRIES = 20;

/**
 * Bot-Profil: "Über mich" in the bot's Discord profile + the rotating status
 * lines (web-api lib/botProfile.ts, bot lib/presence.js). Name and avatar
 * are Discord's — shown here, changed in the Developer Portal.
 */
function BotProfileCard({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin-bot-profile'],
    queryFn: () => api<BotProfile>('/api/admin/bot-profile'),
  });
  const [description, setDescription] = useState('');
  const [presence, setPresence] = useState<PresenceConfig | null>(null);

  useEffect(() => {
    if (!query.data) return;
    setDescription(query.data.description);
    setPresence(query.data.presence);
  }, [query.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/api/admin/bot-profile', { method: 'PUT', body: JSON.stringify({ description, presence }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-bot-profile'] }),
  });

  const selectClass = 'h-10 rounded-md border border-input bg-background px-3 text-sm';
  const setEntry = (i: number, patch: Partial<PresenceConfig['entries'][number]>) =>
    setPresence((p) => p && { ...p, entries: p.entries.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  const valid =
    !!presence &&
    presence.entries.length > 0 &&
    presence.entries.every((e) => e.text.trim().length > 0) &&
    presence.intervalSec >= 15 &&
    presence.intervalSec <= 3600;

  const statusCount = query.data?.presence.entries.length ?? 0;

  return (
    <FoldCard
      id="bot-profile"
      open={open}
      onToggle={onToggle}
      icon={<UserCircle className="h-5 w-5 shrink-0 text-primary" />}
      title="Bot-Profil"
      summary={
        query.data
          ? `${query.data.name} · ${statusCount} Status-${statusCount === 1 ? 'Text' : 'Texte'}`
          : '„Über mich“ und Status des Bots'
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          „Über mich“ und Status des Bots in Discord. Bild und Name änderst du im Discord Developer Portal — das
          Dashboard übernimmt sie automatisch.
        </p>
        {query.isLoading && <p className="text-sm text-muted-foreground">Lade…</p>}
        {query.error && <p className="text-sm text-destructive">{(query.error as Error).message}</p>}
        {query.data && presence && (
          <>
            <div className="flex items-center gap-3">
              {query.data.avatarUrl ? (
                <img src={query.data.avatarUrl} alt="" className="h-12 w-12 rounded-full" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Bot className="h-5 w-5" />
                </div>
              )}
              <div className="text-lg font-semibold">{query.data.name}</div>
            </div>

            <div>
              <Label>Über mich</Label>
              <Textarea
                rows={3}
                maxLength={400}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Was der Bot kann — erscheint in seinem Discord-Profil."
              />
              <div className="mt-1 text-right text-xs text-muted-foreground">{description.length}/400</div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Online-Status</Label>
                <select
                  className={`${selectClass} w-full`}
                  value={presence.status}
                  onChange={(e) => setPresence({ ...presence, status: e.target.value as PresenceConfig['status'] })}
                >
                  <option value="online">Online</option>
                  <option value="idle">Abwesend</option>
                  <option value="dnd">Bitte nicht stören</option>
                </select>
              </div>
              <div>
                <Label>Wechsel alle … Sekunden</Label>
                <Input
                  type="number"
                  min={15}
                  max={3600}
                  value={presence.intervalSec}
                  onChange={(e) => setPresence({ ...presence, intervalSec: Number(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status-Texte (wechseln der Reihe nach)</Label>
              {presence.entries.map((entry, i) => (
                <div key={i} className="flex gap-2">
                  <select
                    className={`${selectClass} w-36 shrink-0`}
                    value={entry.type}
                    onChange={(e) => setEntry(i, { type: e.target.value as PresenceType })}
                  >
                    {PRESENCE_TYPES.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <Input
                    value={entry.text}
                    maxLength={128}
                    onChange={(e) => setEntry(i, { text: e.target.value })}
                    placeholder="z. B. /help oder auf {servers} Servern"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Entfernen"
                    disabled={presence.entries.length === 1}
                    onClick={() => setPresence({ ...presence, entries: presence.entries.filter((_, j) => j !== i) })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={presence.entries.length >= MAX_ENTRIES}
                  onClick={() =>
                    setPresence({ ...presence, entries: [...presence.entries, { type: 'playing', text: '' }] })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Zeile hinzufügen
                </Button>
                <span className="text-xs text-muted-foreground">
                  Platzhalter: <code>{'{servers}'}</code> = Anzahl Server, <code>{'{members}'}</code> = Mitglieder
                  aller Server. Während Musik läuft, zeigt der Bot den Song.
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              {save.isSuccess && !save.isPending && <span className="text-sm text-success">Gespeichert ✓</span>}
              <Button disabled={!valid || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Speichere…' : 'Speichern'}
              </Button>
            </div>
            {save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>}
          </>
        )}
      </div>
    </FoldCard>
  );
}

/**
 * Server klonen: copies the chosen areas of one server onto another (bot
 * guild-clone). The target gets a full safety backup first; its name must be
 * typed in to confirm.
 */
function CloneCard({ guilds, open, onToggle }: { guilds: AdminGuild[]; open: boolean; onToggle: () => void }) {
  const qc = useQueryClient();
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [areas, setAreas] = useState<BackupArea[]>(DEFAULT_AREAS);
  const [confirmName, setConfirmName] = useState('');
  const target = guilds.find((g) => g.id === targetId);
  const source = guilds.find((g) => g.id === sourceId);
  const runs = useRuns();
  const run = useShownRun((r) => r.action === 'guild-clone');
  const busy = lockingRun(runs.data, [targetId, sourceId].filter(Boolean));
  const nameOf = (id: string | null) => guilds.find((g) => g.id === id)?.name ?? 'unbekannter Server';
  useScrollToHash(!!runs.data);

  const clone = useMutation({
    mutationFn: () =>
      api<{ run: BotRun }>('/api/admin/clone', {
        method: 'POST',
        body: JSON.stringify({ sourceGuildId: sourceId, targetGuildId: targetId, areas, confirmName }),
      }),
    onSuccess: (res) => {
      upsertRun(qc, res.run);
      setConfirmName('');
    },
  });

  const selectClass = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';
  const ready = !!source && !!target && source.id !== target.id && areas.length > 0 && confirmName.trim() === target.name;

  const running = run?.status === 'running';

  return (
    <FoldCard
      id="clone"
      open={open}
      onToggle={onToggle}
      icon={<Copy className="h-5 w-5 shrink-0 text-primary" />}
      title="Server klonen"
      summary={
        running
          ? `Läuft: „${nameOf(run.sourceGuildId)}“ → „${nameOf(run.guildId)}“`
          : 'Bereiche von einem Server auf einen anderen übertragen'
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Überträgt die gewählten Bereiche von einem Server auf einen anderen. Der Bot muss auf beiden sein. Vom
          Ziel wird vorher automatisch ein komplettes Backup gemacht; der Servername bleibt.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Von (Quelle)</Label>
            <select className={selectClass} value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              <option value="">— Server wählen —</option>
              {guilds.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>Nach (Ziel)</Label>
            <select className={selectClass} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">— Server wählen —</option>
              {guilds
                .filter((g) => g.id !== sourceId)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <AreaSwitches value={areas} onChange={setAreas} disabled={clone.isPending} />
        {target && (
          <div className="space-y-2 rounded-md border border-destructive/60 p-2 text-sm">
            {areas.includes('structure') ? (
              <p className="text-destructive">
                Auf <b>{target.name}</b> werden alle Kanäle und Rollen gelöscht und nach dem Vorbild von{' '}
                <b>{source?.name ?? '…'}</b> neu aufgebaut; Panels werden neu gepostet.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Ohne „Rollen &amp; Channels“ wird auf <b>{target.name}</b> nichts gelöscht — Einstellungen landen auf
                Rollen und Kanälen mit gleichem Namen.
              </p>
            )}
            <div>
              <div className="mb-1 text-xs text-muted-foreground">
                Zum Bestätigen den Namen des Ziel-Servers eintippen: <b>{target.name}</b>
              </div>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={target.name} />
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {busy && (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" />
              Gesperrt: auf „{nameOf(busy.guildId)}“ läuft {busy.action === 'guild-clone' ? 'Klonen' : 'Wiederherstellen'}{' '}
              (gestartet von {busy.requestedByName})
            </span>
          )}
          <Button variant="destructive" disabled={!ready || clone.isPending || !!busy} onClick={() => clone.mutate()}>
            {clone.isPending ? 'Starte…' : 'Klonen starten'}
          </Button>
        </div>
        {clone.error && <p className="text-sm text-destructive">{(clone.error as Error).message}</p>}
        {run && (
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">
              „{nameOf(run.sourceGuildId)}“ → „{nameOf(run.guildId)}“
            </div>
            <RunProgress run={run} />
          </div>
        )}
      </div>
    </FoldCard>
  );
}

/** Back-link for the pages reached from the Admin-Panel (Zugriff, Live Log). */
export function AdminBackLink() {
  return (
    <Link
      to="/admin"
      className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" /> Zurück zum Admin-Panel
    </Link>
  );
}

function StatTile({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-2xl font-semibold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}
